/**
 * MCP client — communicates with the Convex HTTP Action gateway.
 *
 * Dual mode:
 *   stdio — single user, API key from env, module-level state
 *   http  — multi-user, API key from AsyncLocalStorage, per-key state
 *
 * Configuration:
 *   PRODUCTBRAIN_API_KEY  — pb_sk_* key (stdio mode; http mode gets it per-request)
 *   CONVEX_SITE_URL       — (optional) Convex deployment URL, defaults to cloud
 */

import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";
// WP-575 (TEN-2917): budget DECLARED at the route contract, delivered as a drift-checked copy.
import { latencyBudgetMsForRoute } from "./generated/routeLatencyBudget.generated.js";
// WP-575 / STD-2: what a call COST and how it FAILED lives in ./gatewaySeam.ts — this file
// makes the call, that one records it. Re-exported below so existing importers still resolve.
import { KernelCallError, emptyBodyOrThrowClassified, recordGatewayCall, throwClassifiedGatewayFailure } from "./gatewaySeam.js"; // GatewayTimeoutError/routeMayMutate deliberately absent: re-exported below (needs no import) and matched by `name`, respectively (review round 3).
export {
  GatewayTimeoutError,
  KernelCallError,
  getAuditLog,
  getGatewaySeamCounters,
  formatGatewaySeamSummary,
  __resetGatewaySeamCountersForTest,
  type AuditEntry,
  type GatewaySeamCounters,
} from "./gatewaySeam.js";
import { AsyncLocalStorage } from "node:async_hooks";
import { trackCompoundToolAction } from "./analytics.js";
import { getRequestApiKey, getRequestMcpSessionId, getKeyState, hashKey, type KeyState } from "./auth.js";
import type { NextAction } from "./envelope.js";
import { MCP_NPX_PACKAGE } from "./cli/config-writer.js";
import { warnOnProdFallthrough } from "./prod-fallthrough.js";
import { resolveConversationId } from "./lib/conversation.js";
import type { AgentSessionStartNotice } from "./lib/sessionNotices.js"; // WP-584 offset (STD-2/DEC-1504)
import { recordToolAction } from "./lib/toolActionCounts.js"; // WP-584 offset (STD-2/DEC-1504)
import { parseFallbackUrls, probeDeploymentCandidates } from "./lib/deploymentUrlResolver.js"; // WP-575 offset (STD-2/DEC-1504)

// ─── Conversation identity (WP-479 E2) ─────────────────────────────────

// The MCP server is a subprocess of the harness (Claude Code / Cursor / Codex) — it resolves its
// conversation identity ONCE at process startup from its own env and holds it for the process
// lifetime (there is no per-request or per-key variation: one MCP server process is one
// conversation). `undefined` means "not yet resolved"; `null` is a legitimate resolved value
// (no identity signal present — legacy behavior).
let _conversationId: string | null | undefined;

export function getConversationId(): string | null {
  // In HTTP transport mode a single process multiplexes many `Mcp-Session-Id` streams. The
  // env-derived conversation id is process-global, so it can't identify a stream — and returning null
  // (an earlier fix) is worse: `startSession`'s null-conversation reuse then collapses every HTTP
  // stream sharing an API key onto ONE server `agentSessions` row (Codex P1). The `Mcp-Session-Id` IS
  // the stable per-stream identity, so use it as the conversation id for HTTP starts/recovery — each
  // stream becomes its own server-side conversation. STDIO (one process = one conversation) keeps the
  // env-derived id.
  const mcpSid = getRequestMcpSessionId();
  if (mcpSid) return `mcp:${mcpSid}`;
  if (getRequestApiKey()) return null; // HTTP request before a session id is assigned (initialize)
  if (_conversationId === undefined) _conversationId = resolveConversationId();
  return _conversationId;
}

// ─── Tool Context (for audit action logging) ─────────────────────────────

const toolContextStore = new AsyncLocalStorage<{ tool: string; action?: string }>();

/**
 * Run a callback with tool context for audit logging.
 * Compound tools should wrap their handler with this so workspace action=audit can distinguish
 * e.g. entries action=get from entries action=search.
 */
export function runWithToolContext<T>(
  ctx: { tool: string; action?: string },
  fn: () => T | Promise<T>,
): T | Promise<T> {
  recordToolAction(ctx.tool, ctx.action);
  trackCompoundToolAction(ctx.tool, ctx.action, state().workspaceId ?? "unresolved");
  return toolContextStore.run(ctx, fn);
}

function getToolContext(): { tool: string; action?: string } | null {
  return toolContextStore.getStore() ?? null;
}

export const DEFAULT_CLOUD_URL = "https://gateway.productbrain.io";

// ─── Read Cache (Batch A: sub-200ms repeat calls) ─────────────────────

const CACHE_TTL_MS = 60_000; // 60s per plan
const CACHEABLE_FNS = [
  "chain.getOrientEntries",
  "chain.gatherContext",
  "chain.graphGatherContext",
  "chain.taskAwareGatherContext",
  "chain.journeyAwareGatherContext",
  "chain.assembleBuildContext",
] as const;

function isCacheable(fn: string): boolean {
  return (CACHEABLE_FNS as readonly string[]).includes(fn);
}

// PR #405 review (Codex P2): `chain.evaluateCoherence` (read-only) fell through this pattern
// (no `evaluate` verb recognized), misclassifying every successful coherence check on the
// orient/start/wrapup hot path as a write and discarding cacheable results for no reason.
// PR #517 round 1 (Codex P2): same fix for `chain.shapeAdvisories`/`chain.showShapeAdvisory`.
// PR #519 review round 3 (Codex P2): `chain.shapeAdvisorySummary` (orientShapeAdvisorySummary,
// read-only) fell through this same gap — a successful read was misclassified as a write,
// clearing the shared 60s orient/context cache for every workspace in the process.
const READ_PATTERN =
  /^(chain\.(get|list|search|batchGet|gather|graph|task|journey|assemble|workspace|score|absence|evaluate|shapeAdvisories|showShapeAdvisory|shapeAdvisorySummary)|chainwork\.(get|list|score)|maps\.(get|list)|gitchain\.(get|list|diff|history|runGate))/i;

function isWrite(fn: string): boolean {
  if (fn.startsWith("agent.")) return false;
  return !READ_PATTERN.test(fn);
}

interface CacheEntry<T> {
  data: T;
  expiresAt: number;
}

const readCache = new Map<string, CacheEntry<unknown>>();

function cacheKey(fn: string, args: Record<string, unknown>): string {
  return `${fn}:${JSON.stringify(args)}`;
}

function getCached<T>(fn: string, args: Record<string, unknown>): T | undefined {
  if (!isCacheable(fn)) return undefined;
  const key = cacheKey(fn, args);
  const entry = readCache.get(key) as CacheEntry<T> | undefined;
  if (!entry || Date.now() > entry.expiresAt) {
    if (entry) readCache.delete(key);
    return undefined;
  }
  return entry.data;
}

function setCached<T>(fn: string, args: Record<string, unknown>, data: T): void {
  if (!isCacheable(fn)) return;
  const key = cacheKey(fn, args);
  readCache.set(key, { data, expiresAt: Date.now() + CACHE_TTL_MS });
}

function invalidateReadCache(): void {
  readCache.clear();
}

// ─── State Management ─────────────────────────────────────────────────

const _stdioState: KeyState = {
  workspaceId: null,
  workspaceSlug: null,
  workspaceName: null,
  workspaceCreatedAt: null,
  workspaceGovernanceMode: null,
  agentSessionId: null,
  apiKeyId: null,
  apiKeyScope: "readwrite",
  sessionOriented: false,
  sessionClosed: false,
  lastAccess: 0,
  deploymentUrl: null,
};

/**
 * Returns the active client state.
 * stdio: module-level singleton. http: per-API-key state from AsyncLocalStorage.
 */
function state(): KeyState {
  const reqKey = getRequestApiKey();
  if (reqKey) return getKeyState(reqKey);
  return _stdioState;
}

/**
 * Cache partition key for the current request.
 *   http  — hashed API key (per-workspace isolation; hashKey is already used for
 *           session binding in auth.ts, so the raw secret is never used as a map key).
 *   stdio — fixed "stdio" sentinel (single user per process).
 *
 * The OAuth access_token IS the permanent pb_sk_ API key (stable per workspace
 * across refreshes — TEN-1143), so this key never churns mid-session.
 * Every per-workspace cache (collectionCache, smart-capture profile/hub) keys on this.
 */
export function cacheScope(): string {
  const key = getRequestApiKey();
  return key ? hashKey(key) : "stdio";
}

/**
 * Returns the active API key (request-scoped in HTTP mode, env in stdio mode).
 */
function getActiveApiKey(): string {
  const fromRequest = getRequestApiKey();
  if (fromRequest) return fromRequest;
  const fromEnv = process.env.PRODUCTBRAIN_API_KEY;
  if (!fromEnv) throw new Error("No API key available — set PRODUCTBRAIN_API_KEY or provide Bearer token");
  return fromEnv;
}

// ─── Agent Session State ──────────────────────────────────────────────

/**
 * WP-479 review fix (Codex re-review): the SESSION-LIFECYCLE fields (active agentSessionId +
 * oriented/closed flags) must be isolated per HTTP `Mcp-Session-Id`, not shared across a key's
 * concurrent streams — otherwise two streams sharing a key overwrite each other's active session and
 * later orient/write calls use the sibling's session. Workspace + cache state legitimately stays
 * per-API-key on `state()`; only these three fields move to a per-stream store. In STDIO mode (or the
 * pre-session initialize request) there is no Mcp-Session-Id, so they stay on the singleton/per-key
 * `state()` — one process/one key is one session there.
 */
interface AgentSessionLifecycle {
  agentSessionId: string | null;
  sessionOriented: boolean;
  sessionClosed: boolean;
}
const _sessionLifecycleByStream = new Map<string, AgentSessionLifecycle>();
const MAX_SESSION_STREAMS = 500;

function sessionLifecycle(): AgentSessionLifecycle {
  const mcpSid = getRequestMcpSessionId();
  if (!mcpSid) return state(); // STDIO / pre-session — the per-key/singleton state carries them
  const key = `${cacheScope()}:${mcpSid}`;
  let lc = _sessionLifecycleByStream.get(key);
  if (!lc) {
    // Bound memory: a terminated stream's entry is stale; FIFO-evict the oldest when full.
    if (_sessionLifecycleByStream.size >= MAX_SESSION_STREAMS) {
      const oldest = _sessionLifecycleByStream.keys().next().value;
      if (oldest !== undefined) _sessionLifecycleByStream.delete(oldest);
    }
    lc = { agentSessionId: null, sessionOriented: false, sessionClosed: false };
    _sessionLifecycleByStream.set(key, lc);
  }
  return lc;
}

export function getAgentSessionId(): string | null {
  return sessionLifecycle().agentSessionId;
}

export function isSessionOriented(): boolean {
  return sessionLifecycle().sessionOriented;
}

export function setSessionOriented(value: boolean): void {
  sessionLifecycle().sessionOriented = value;
}

export function getApiKeyScope(): "read" | "readwrite" {
  return state().apiKeyScope;
}

export function isSessionClosed(): boolean {
  return sessionLifecycle().sessionClosed;
}

export interface AgentSessionStartResult {
  sessionId: string;
  initiatedBy: string;
  toolsScope: "read" | "readwrite";
  workspaceName: string;
  feedbackQueueNew?: number; feedbackOldestNewAt?: number;
  notices?: AgentSessionStartNotice[];
  adoptDiscoveryHint?: string; // WP-638 S4 §10.3 — "3" or, at the scan cap, "20+".
}

/**
 * Start an agent session. Creates a session record in Convex.
 * toolsScope is derived server-side from the API key — not passed as a parameter.
 * WP-479 E2: concurrent sessions on one key are expected, not collapsed — an existing active
 * session is never superseded; per-key quota is enforced instead (TEN-2570).
 */
export async function startAgentSession(): Promise<AgentSessionStartResult> {
  const workspaceId = await getWorkspaceId();
  const s = state();
  if (!s.apiKeyId) {
    throw new Error("Cannot start session: API key ID not resolved. Ensure workspace resolution completed.");
  }

  const result = await kernelCall<GatewayRouteReturnByName<"agent.startSession">>("agent.startSession", {
    workspaceId,
    apiKeyId: s.apiKeyId,
    clientKind: "mcp",
    // WP-479 E2: resolved once at process startup and held — see getConversationId() above.
    conversationId: getConversationId() ?? undefined,
  });

  // Session-lifecycle fields are per-Mcp-Session-Id (Codex re-review); apiKeyScope stays per-key.
  const lc = sessionLifecycle();
  if (lc.agentSessionId) {
    resetTouchThrottle(lc.agentSessionId);
  }
  lc.agentSessionId = result.sessionId;
  s.apiKeyScope = result.toolsScope;
  lc.sessionOriented = false;
  lc.sessionClosed = false;
  resetTouchThrottle(result.sessionId);

  return result;
}

/**
 * Close the current agent session. After this, write tools are blocked
 * even if the MCP connection stays open.
 */
export async function closeAgentSession(): Promise<void> {
  const lc = sessionLifecycle();
  if (!lc.agentSessionId) return;
  const sessionId = lc.agentSessionId;
  try {
    await kernelCall<GatewayRouteReturnByName<"agent.closeSession">>("agent.closeSession", {
      sessionId,
      status: "closed",
    });
  } finally {
    resetTouchThrottle(sessionId);
    lc.sessionClosed = true;
    lc.agentSessionId = null;
    lc.sessionOriented = false;
  }
}

/**
 * Mark current session as orphaned (used on disconnect/crash).
 */
export async function orphanAgentSession(): Promise<void> {
  const lc = sessionLifecycle();
  if (!lc.agentSessionId) return;
  const sessionId = lc.agentSessionId;
  try {
    await kernelCall<GatewayRouteReturnByName<"agent.closeSession">>("agent.closeSession", {
      sessionId,
      status: "orphaned",
    });
  } catch {
    // Best-effort on disconnect
  } finally {
    resetTouchThrottle(sessionId);
    lc.agentSessionId = null;
    lc.sessionOriented = false;
  }
}

/**
 * Touch the session to update lastToolCallAt. Fire-and-forget.
 * Throttled to at most once per 5s to prevent OCC conflicts when
 * multiple tool calls complete in parallel.
 */
const _lastTouchAtBySession = new Map<string, number>();
const TOUCH_THROTTLE_MS = 5_000;

export function touchSessionActivity(): void {
  const sessionId = sessionLifecycle().agentSessionId;
  if (!sessionId) return;

  const now = Date.now();
  const lastTouchAt = _lastTouchAtBySession.get(sessionId) ?? 0;
  if (now - lastTouchAt < TOUCH_THROTTLE_MS) return;
  _lastTouchAtBySession.set(sessionId, now);

  kernelCall<GatewayRouteReturnByName<"agent.touchSession">>("agent.touchSession", {
    sessionId,
  }).catch(() => {});
}

export function resetTouchThrottle(sessionId?: string | null): void {
  if (sessionId) {
    _lastTouchAtBySession.delete(sessionId);
    return;
  }
  _lastTouchAtBySession.clear();
}

/**
 * Record structured activity on the current session.
 */
export async function recordSessionActivity(activity: {
  entryCreated?: string;
  entryModified?: string;
  relationCreated?: boolean;
  gateFailure?: boolean;
  contradictionWarning?: boolean;
  strategyLinkWarnedForEntryId?: string;
}): Promise<void> {
  const sessionId = sessionLifecycle().agentSessionId;
  if (!sessionId) return;
  try {
    await kernelCall<GatewayRouteReturnByName<"agent.recordActivity">>("agent.recordActivity", {
      sessionId,
      ...activity,
    });
  } catch {
    // Non-critical — don't fail the tool call over activity tracking
  }
}

// ─── Audit ────────────────────────────────────────────────────────────

/**
 * Bootstrap for stdio mode: set CONVEX_SITE_URL default and warn on missing key.
 * API key is validated lazily on first kernelCall so the server can start and handle
 * signals (SIGTERM) even before credentials are provided (e.g. in tests).
 */
export function bootstrap(): void {
  const explicit = process.env.CONVEX_SITE_URL ?? process.env.PRODUCTBRAIN_URL;
  process.env.CONVEX_SITE_URL ??= process.env.PRODUCTBRAIN_URL ?? DEFAULT_CLOUD_URL;
  warnOnProdFallthrough(process.env.CONVEX_SITE_URL, { explicit: explicit != null });
  const pbKey = process.env.PRODUCTBRAIN_API_KEY;
  if (!pbKey?.startsWith("pb_sk_")) {
    process.stderr.write(
      "[MCP] Warning: PRODUCTBRAIN_API_KEY is not set or invalid. " +
      "Tool calls will fail until a valid key is provided.\n"
    );
  }
}

/**
 * Bootstrap for HTTP mode: only set CONVEX_SITE_URL.
 * API key validation happens per-request via Bearer token.
 */
export function bootstrapHttp(): void {
  const explicit = process.env.CONVEX_SITE_URL ?? process.env.PRODUCTBRAIN_URL;
  process.env.CONVEX_SITE_URL ??= process.env.PRODUCTBRAIN_URL ?? DEFAULT_CLOUD_URL;
  warnOnProdFallthrough(process.env.CONVEX_SITE_URL, { explicit: explicit != null });
}

/** @deprecated Use bootstrap() instead. Alias kept for callers in transition. */
export const bootstrapCloudMode = bootstrap;

function getEnv(key: string): string {
  const value = process.env[key];
  if (!value) throw new Error(`${key} environment variable is required`);
  return value;
}

/**
 * DEC-789 S2: Resolve the Convex deployment URL for the active API key.
 *
 * Warm path (normal): key-check already ran during OAuth authorize, so
 * state().deploymentUrl is set — return it immediately.
 *
 * No-fallback path (default / single-deployment): when CONVEX_FALLBACK_URLS
 * is not set, return CONVEX_SITE_URL directly without probing. This preserves
 * the original single-URL behavior and avoids extra fetch calls in tests and
 * stdio mode.
 *
 * Cold-start path (after Railway restart with CONVEX_FALLBACK_URLS configured):
 * keyStateMap was wiped; probe candidate URLs (lib/deploymentUrlResolver.ts) in order until
 * one responds ok:true to /api/key-check, store the result so subsequent calls are instant.
 *
 * Must be called from within a runWithAuth context (state() and
 * getActiveApiKey() both require it).
 */
async function resolveDeploymentUrl(): Promise<string> {
  const s = state();
  if (s.deploymentUrl) return s.deploymentUrl;

  const primaryUrl = (process.env.CONVEX_SITE_URL ?? DEFAULT_CLOUD_URL).replace(/\/$/, "");
  const fallbacks = parseFallbackUrls(process.env.CONVEX_FALLBACK_URLS);

  // No fallbacks configured — single-deployment setup, use primary directly.
  // This is the common case: preserves the original behavior, no extra fetch calls.
  if (fallbacks.length === 0) {
    return primaryUrl;
  }

  // Multi-deployment cold-start: probe candidates in order.
  const candidates = [primaryUrl, ...fallbacks.map((u) => u.replace(/\/$/, ""))];

  let apiKey: string;
  try {
    apiKey = getActiveApiKey();
  } catch {
    // No API key available — return primary and let the tool call fail normally.
    return primaryUrl;
  }

  const found = await probeDeploymentCandidates(candidates, apiKey);
  if (found) {
    s.deploymentUrl = found;
    return found;
  }

  // All probes failed — return the first candidate and let the tool call fail with its normal error.
  return candidates[0];
}

/**
 * Thin adapter over ./gatewaySeam.ts's `recordGatewayCall` — supplies the two pieces of
 * per-call context that module deliberately does not reach for (see its header).
 *
 * PR #533 review, Codex P2: the workspace fallback used to be the literal string
 * "unresolved" — a single bucket EVERY caller whose workspace hadn't resolved yet shared. In
 * HTTP mode that's still a cross-tenant collision (two different not-yet-resolved API keys
 * would land in the same "unresolved" audit/counters bucket), just a narrower one than the
 * original unfiltered leak. `cacheScope()` already exists for exactly this shape of problem
 * (hashed per-API-key in HTTP mode, a fixed "stdio" sentinel in stdio mode — see its own doc
 * comment) and is what collectionCache/smart-capture already partition on, so reuse it here
 * instead of inventing a second per-caller identity scheme.
 */
function audit(
  fn: string,
  status: "ok" | "error",
  durationMs: number,
  errorMsg?: string,
  meta?: { budgetMs?: number; timedOut?: boolean },
): void {
  recordGatewayCall({
    fn, status, durationMs, errorMsg,
    workspace: state().workspaceId ?? cacheScope(),
    toolContext: getToolContext(),
    budgetMs: meta?.budgetMs,
    timedOut: meta?.timedOut,
  });
}

// ─── HTTP Client ──────────────────────────────────────────────────────

// NextAction imported from ./envelope.js — local MCP-layer definition (mirrors convex/lib/envelopeContract.ts;
// BR-113 prevents direct convex/ imports from MCP package, so shape is kept in sync manually)

interface GatewaySuccessResponse<T> {
  ok: true;
  summary: string;
  data: T;
  next?: NextAction[];
  _meta?: { durationMs?: number };
}

interface GatewayErrorResponse {
  ok: false;
  code?: string;
  message?: string;
  error?: string;
  missingRequiredFields?: string[];
  fieldErrors?: unknown[];
  /** WP-465 slice ⑤: structured per-offender diagnostics (coherencyRefusals, blockers, …). */
  diagnostics?: Record<string, unknown>;
}

type GatewayResponse<T> = GatewaySuccessResponse<T> | GatewayErrorResponse;

// STD-101: Single SSOT for TOUCH_EXCLUDED — shared by kernelCall (callGateway) and kernelCallEnvelope.
// Exported for testability — tests assert membership to prevent OCC cascade regressions.
export const TOUCH_EXCLUDED = new Set([
  "agent.touchSession",
  "agent.startSession",
  "agent.markOriented",
  "agent.recordActivity",
  "agent.recordWrapup",
  "agent.closeSession",
  // WP-376 α.3: orient byte report is itself a heartbeat-equivalent observability
  // write that already patches the same agentSessions row. A follow-up touchSession
  // would create a redundant second write per orient call (DEC-50 OCC anti-pattern).
  "agent.reportOrientMetric",
]);

/**
 * Private: shared HTTP fetch + error handling for kernelCall and kernelCallEnvelope.
 * Returns the parsed envelope fields without touching the read cache or session.
 */
/**
 * Connector identity for the gateway's context.served shared seam (DEC-1207). Sent as the
 * `x-pb-source` header so the server-side emit attributes MCP — the dominant agent surface
 * (INS-1706) that emitted nothing under the prior CLI-only seam.
 */
const MCP_TELEMETRY_SOURCE = "mcp";

async function callGateway<T>(fn: string, args: Record<string, unknown>): Promise<{
  data: T;
  summary: string;
  next?: NextAction[];
  _meta?: { durationMs?: number };
}> {
  const siteUrl = await resolveDeploymentUrl();
  const apiKey = getActiveApiKey();

  // WP-575 (TEN-2917): the budget is DECLARED at the route contract
  // (packages/kernel-client/src/routeLatencyBudget.ts) and reaches this connector as a
  // drift-checked generated copy. It replaces the blanket `AbortSignal.timeout(10_000)`
  // that used to apply to every route alike — including `chain.createEntry`, whose real
  // 14-20s round-trip meant this client reported hard failures on writes that had SUCCEEDED.
  const budgetMs = latencyBudgetMsForRoute(fn);
  const start = Date.now();

  let res: Response;
  try {
    res = await fetch(`${siteUrl}/api/aki`, {
      method: "POST",
      signal: AbortSignal.timeout(budgetMs),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
        // DEC-1207: attribute this connector for the gateway's context.served seam. MCP is
        // the dominant agent surface (INS-1706); without this header it was emitting nothing.
        "x-pb-source": MCP_TELEMETRY_SOURCE,
      },
      body: JSON.stringify({ fn, args }),
    });
  } catch (err: any) {
    throwClassifiedGatewayFailure(err, fn, args, budgetMs, Date.now() - start, "network",
      (m, t) => audit(fn, "error", Date.now() - start, m, { budgetMs, timedOut: t }));
  }

  // WP-575 / PR #533 review (Codex P2): the abort signal is STILL LIVE while the body streams,
  // and `fetch` resolves at headers — so a slow body can blow the budget HERE, outside the try
  // above, and escape as a raw DOMException nothing classifies. Same treatment, later phase.
  let json: GatewayResponse<T>;
  try {
    json = (await res.json()) as GatewayResponse<T>;
  } catch (err: any) {
    json = emptyBodyOrThrowClassified<GatewayResponse<T>>(err, fn, args, budgetMs, Date.now() - start, res.ok,
      (m, t) => audit(fn, "error", Date.now() - start, m, { budgetMs, timedOut: t }));
  }

  if (!res.ok || json.ok === false) {
    const errJson = json as GatewayErrorResponse;
    const msg = errJson.error ?? errJson.message ?? "unknown error";
    audit(fn, "error", Date.now() - start, errJson.code ? `${msg} [${errJson.code}]` : msg, { budgetMs });
    throw new KernelCallError(
      `MCP call "${fn}" failed (${res.status}): ${msg}`,
      res.status,
      errJson.code,
      Array.isArray(errJson.missingRequiredFields) ? errJson.missingRequiredFields : undefined,
      Array.isArray(errJson.fieldErrors) ? (errJson.fieldErrors as string[]) : undefined,
      errJson.diagnostics && typeof errJson.diagnostics === "object" ? errJson.diagnostics : undefined,
    );
  }

  audit(fn, "ok", Date.now() - start, undefined, { budgetMs });

  const { data, summary, next, _meta } = json as GatewaySuccessResponse<T>;
  return {
    data: data as T,
    summary: summary || fn,
    next,
    _meta,
  };
}

/**
 * Low-level call to the HTTP Action gateway.
 * Workspace scoping is enforced server-side from the API key — callers
 * don't need to (and can't) override the workspace.
 *
 * Read cache: orient and context-gather responses are cached for 60s.
 * Cache is invalidated on any write (safe-by-default: everything not
 * matching a known read pattern is treated as a write).
 */
export async function kernelCall<T>(fn: string, args: Record<string, unknown> = {}): Promise<T> {
  const cached = getCached<T>(fn, args);
  if (cached !== undefined) {
    return cached;
  }

  const { data } = await callGateway<T>(fn, args);

  if (isWrite(fn)) {
    invalidateReadCache();
  } else {
    setCached(fn, args, data);
  }

  // Exclude session bookkeeping calls from the heartbeat touch. These mutations
  // already target the active session document, so immediately heartbeating after
  // them only adds avoidable OCC pressure on the same row.
  if (getAgentSessionId() && !TOUCH_EXCLUDED.has(fn)) {
    touchSessionActivity();
  }

  return data;
}

/**
 * Calls the gateway and returns the kernel success envelope
 * ({ ok: true, summary, data, next?, _meta? }) instead of stripping to just data.
 *
 * Use for MCP tools that need thin passthrough — forwarding summary, next actions,
 * or timing metadata to the MCP client.
 *
 * Note: Throws KernelCallError on gateway errors (non-200 responses). Full
 * envelope error passthrough (ok:false at HTTP 200) deferred to WP-321 S6+
 * when DEC-571 HTTP semantics are activated on error paths.
 *
 * Does NOT use the read cache (kernel _meta.durationMs would be stale from cache;
 * thin passthrough tools need fresh timing). Does NOT call invalidateReadCache
 * (read-only by contract).
 */
export async function kernelCallEnvelope<T>(
  fn: string,
  args: Record<string, unknown> = {},
): Promise<{
  ok: true;
  summary: string;
  data: T;
  next?: NextAction[];
  _meta?: { durationMs?: number };
}> {
  const { data, summary, next, _meta } = await callGateway<T>(fn, args);

  if (getAgentSessionId() && !TOUCH_EXCLUDED.has(fn)) {
    touchSessionActivity();
  }

  return { ok: true, summary, data, next, _meta };
}

// ─── Workspace Resolution ─────────────────────────────────────────────

const resolveInFlightMap = new Map<string, Promise<string>>();

export async function getWorkspaceId(): Promise<string> {
  const s = state();
  if (s.workspaceId) return s.workspaceId;

  const apiKey = getActiveApiKey();
  const existing = resolveInFlightMap.get(apiKey);
  if (existing) return existing;

  const promise = resolveWorkspaceWithRetry().finally(() => resolveInFlightMap.delete(apiKey));
  resolveInFlightMap.set(apiKey, promise);
  return promise;
}

async function resolveWorkspaceWithRetry(maxRetries = 2): Promise<string> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const workspace = await kernelCall<{
        _id: string;
        name: string;
        slug: string;
        createdAt?: number;
        keyScope?: string;
        keyId?: string;
        governanceMode?: "open" | "consensus" | "role";
      } | null>("resolveWorkspace", {});

      if (!workspace) {
        throw new Error(
          "API key is valid but no workspace is associated. " +
          `Run \`npx ${MCP_NPX_PACKAGE} setup\` or regenerate your key.`
        );
      }

      const s = state();
      s.workspaceId = workspace._id;
      s.workspaceSlug = workspace.slug;
      s.workspaceName = workspace.name;
      s.workspaceCreatedAt = workspace.createdAt ?? null;
      s.workspaceGovernanceMode = workspace.governanceMode ?? "open";
      if (workspace.keyScope) s.apiKeyScope = workspace.keyScope as "read" | "readwrite";
      if (workspace.keyId) s.apiKeyId = workspace.keyId;
      return s.workspaceId;
    } catch (err: any) {
      lastError = err;
      // WP-575 / PR #533 review (Codex P2): a budget abort must stay RETRYABLE. This regex
      // matched the OLD "network error" text, so the typed error silently dropped the retry
      // on the first call every session makes. It is a query — nothing to duplicate.
      const isTransient =
        err?.name === "GatewayTimeoutError" ||
        /network error|fetch failed|ECONNREFUSED|ETIMEDOUT/i.test(err.message);
      if (!isTransient || attempt === maxRetries) break;
      const delay = 1000 * (attempt + 1);
      process.stderr.write(
        `[MCP] Workspace resolution failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms...\n`
      );
      await new Promise((r) => setTimeout(r, delay));
    }
  }

  throw lastError!;
}

export interface WorkspaceContext {
  workspaceId: string;
  workspaceSlug: string;
  workspaceName: string;
  createdAt: number | null;
  /** BET-76 FEAT-111: Cached from workspace resolution. Defaults to 'open'. */
  governanceMode: "open" | "consensus" | "role";
}

export async function getWorkspaceContext(): Promise<WorkspaceContext> {
  const workspaceId = await getWorkspaceId();
  const s = state();
  return {
    workspaceId,
    workspaceSlug: s.workspaceSlug ?? "unknown",
    workspaceName: s.workspaceName ?? "unknown",
    createdAt: s.workspaceCreatedAt,
    governanceMode: s.workspaceGovernanceMode ?? "open",
  };
}

/**
 * TEN-1810: Re-fetches only governanceMode from the workspace without invalidating
 * stable identifiers (workspaceId, slug, name). Safe to call before every capture.
 * Single round-trip, no retry loop — workspace is already known at this point.
 */
export async function refreshWorkspaceGovernanceMode(): Promise<"open" | "consensus" | "role"> {
  const workspace = await kernelCall<{
    governanceMode?: "open" | "consensus" | "role";
  } | null>("resolveWorkspace", {});
  const mode: "open" | "consensus" | "role" = workspace?.governanceMode ?? "open";
  const s = state();
  s.workspaceGovernanceMode = mode;
  return mode;
}

export async function kernelQuery<T>(fn: string, args: Record<string, unknown> = {}): Promise<T> {
  const workspaceId = await getWorkspaceId();
  return kernelCall<T>(fn, { ...args, workspaceId });
}

export async function kernelMutation<T>(fn: string, args: Record<string, unknown> = {}): Promise<T> {
  const workspaceId = await getWorkspaceId();
  return kernelCall<T>(fn, { ...args, workspaceId });
}

/**
 * @deprecated Use kernelQuery (reads) or kernelMutation (writes) instead.
 * Kept temporarily for backward compatibility — identical to both.
 */
export async function mcpAction<T>(fn: string, args: Record<string, unknown> = {}): Promise<T> {
  const workspaceId = await getWorkspaceId();
  return kernelCall<T>(fn, { ...args, workspaceId });
}

/**
 * Gate check: throws if no active, oriented session exists.
 *
 * Used for read tools that require session context per SOS-iszqu7:
 * structured Chain data for agent consumption requires an active session.
 * Lighter than requireWriteAccess — does not check key scope.
 */
export function requireActiveSession(): void {
  const lc = sessionLifecycle();

  if (!lc.agentSessionId) {
    throw new Error(
      "Active session required (SOS-iszqu7). Call `session action=start` then `orient` first."
    );
  }

  if (lc.sessionClosed) {
    throw new Error(
      "Session has been closed (SOS-iszqu7). Start a new session with `session action=start`."
    );
  }

  if (!lc.sessionOriented) {
    throw new Error(
      "Orientation required before accessing build context (SOS-iszqu7). Call `orient` first."
    );
  }
}

/**
 * Gate check: throws if the agent is not allowed to write.
 *
 * Enforces:
 * 1. Session must exist (always required — no REQUIRE_AGENT_SESSION flag)
 * 2. Session must not be closed
 * 3. Session must be oriented
 * 4. Key scope must be readwrite
 */
export function requireWriteAccess(): void {
  const lc = sessionLifecycle();
  const s = state();

  if (!lc.agentSessionId) {
    throw new Error(
      "Agent session required for write operations. Call `session action=start` first."
    );
  }

  if (lc.sessionClosed) {
    throw new Error(
      "Agent session has been closed. Write tools are no longer available."
    );
  }

  if (!lc.sessionOriented) {
    throw new Error(
      "Orientation required before writing to the Chain. Call 'orient' first."
    );
  }

  if (s.apiKeyScope === "read") {
    throw new Error(
      "This API key has read-only scope. Write tools are not available."
    );
  }
}

/**
 * Gate for vendor feedback-triage actions (queue/note/group/status); list is member read-back.
 *
 * Mirrors the server contract exactly (convex/productFeedback.ts
 * `validateVendorTriageSession` → `intelligence/agentSessions:validateSessionForCaller`
 * with `requireActive: true`, `requireScope: 'readwrite'`, and deliberately NO
 * `requireOriented`): feedback rows are vendor telemetry, not Chain entries, and the
 * session-start queue hint points straight at `feedback action=queue` — gating triage
 * on orientation client-side would reject a call the server accepts.
 *
 * Enforces:
 * 1. Session must exist
 * 2. Session must not be closed
 * 3. Key scope must be readwrite
 *
 * Unlike `requireWriteAccess`, orientation is NOT required.
 */
export function requireVendorTriageAccess(): void {
  const lc = sessionLifecycle();
  const s = state();

  if (!lc.agentSessionId) {
    throw new Error(
      "Agent session required for feedback triage. Call `session action=start` first."
    );
  }

  if (lc.sessionClosed) {
    throw new Error(
      "Agent session has been closed. Feedback triage is no longer available."
    );
  }

  if (s.apiKeyScope === "read") {
    throw new Error(
      "This API key has read-only scope. Feedback triage is not available."
    );
  }
}

/**
 * Recover session orientation state from Convex on restart.
 * If the session is active and oriented in Convex, restore local state.
 */
export async function recoverSessionState(): Promise<void> {
  const s = state();
  if (!s.workspaceId) return;
  try {
    const session = await kernelCall<{
      _id: string;
      status: string;
      oriented: boolean;
      toolsScope: string;
    } | null>("agent.getActiveSession", {
      workspaceId: s.workspaceId,
      // WP-479 E2: required disambiguation arg for the per-apiKey-quota re-scoped
      // getActiveSession — absent identity (null) still resolves to the keyless/legacy path
      // server-side.
      conversationId: getConversationId() ?? undefined,
    });

    if (session && session.status === "active") {
      const lc = sessionLifecycle();
      lc.agentSessionId = session._id;
      lc.sessionOriented = session.oriented;
      s.apiKeyScope = session.toolsScope as "read" | "readwrite";
      lc.sessionClosed = false;
    }
  } catch {
    // Recovery is best-effort
  }
}
